import type { RefObject } from 'react';
import React, { useRef } from 'react';
import classNames from 'clsx';
import Link from '@wix/thunderbolt-elements/components/Link';
import {
  formatClassNames,
  isEmptyObject,
} from '@wix/editor-elements-common-utils';
import type {
  DropdownAnimationNameType,
  IMenuItemProps,
} from '../../../Menu.types';
import { createSDKAction } from './utils';
import classes from './style/MenuItem.module.scss';
import variableStyles from './style/VariableStyles.module.scss';
import { MenuParts } from '../../constants';
import { isCurrentItem } from '../../../../../common/menu/getCurrentMenuItem';
import { semanticClassNames as menuSemanticClassNames } from '../../../Menu.semanticClassNames';
import shmSemanticClassNames from '../../../../StylableHorizontalMenu/StylableHorizontalMenu.semanticClassNames';
import { AnimationPhase, type AnimationState } from './useAnimationState';
import { MenuItemDropdownIcon } from './MenuItemDropdownIcon';

const itemLabelClassNames = classNames(
  classes.label,
  formatClassNames(shmSemanticClassNames.menuItemLabel),
  formatClassNames(menuSemanticClassNames.itemLabel),
);

export const MenuItemLabel: React.FC<
  IMenuItemProps & {
    hasDropdownMenu: boolean;
    hasSubItems: boolean;
    chevronButtonRef: any;
    toggleDropdown: () => void;
    isOpen: boolean;
    dropdownAnimationState: AnimationState<DropdownAnimationNameType>;
  }
> = props => {
  const {
    item,
    currentItem,
    className,
    hasDropdownMenu,
    hasSubItems,
    onItemClick,
    onItemDblClick,
    onItemMouseIn,
    onItemMouseOut,
    partToPreviewStateMap,
    translations,
    chevronButtonRef,
    toggleDropdown,
    isOpen,
    dropdownAnimationState,
    layoutClassType,
    withVariableStyles,
  } = props;

  const isCurrentPage = isCurrentItem(item, currentItem);
  const { label, link } = item;
  const hasLink = !isEmptyObject(link);
  const linkRef: RefObject<HTMLAnchorElement> = useRef<HTMLAnchorElement>(null);

  const labelContainerClassNames = classNames(
    className,
    classes.labelContainer,
    classes[layoutClassType],
    withVariableStyles && variableStyles.labelContainer,
    formatClassNames(shmSemanticClassNames.menuItemWrapper),
    formatClassNames(menuSemanticClassNames.item),
  );

  const isInteractive = (hasSubItems || hasLink) && !isCurrentPage;

  const isDropdownIconOpen =
    isOpen && dropdownAnimationState.phase !== AnimationPhase.ExitActive;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { children, ...itemData } = item;

  return (
    <div
      className={labelContainerClassNames}
      data-selected={isCurrentPage ? true : undefined}
      data-part={MenuParts.MenuItemContent}
      data-preview={partToPreviewStateMap?.item}
      data-interactive={isInteractive}
      onClick={createSDKAction(itemData, isCurrentPage, onItemClick)}
      onMouseEnter={createSDKAction(itemData, isCurrentPage, onItemMouseIn)}
      onMouseLeave={createSDKAction(itemData, isCurrentPage, onItemMouseOut)}
      onDoubleClick={createSDKAction(itemData, isCurrentPage, onItemDblClick)}
    >
      <Link
        ref={linkRef}
        {...link}
        dataPart={MenuParts.MenuItemLink}
        activateByKey="Enter"
        {...(isCurrentPage && {
          'aria-current': 'page',
        })}
      >
        <div className={itemLabelClassNames} data-part={MenuParts.Label}>
          {label}
        </div>
      </Link>
      {hasDropdownMenu &&
        ['default', 'srOnly'].map(variant => (
          <MenuItemDropdownIcon
            key={variant}
            ariaLabel={translations.dropdownButtonAriaLabel?.replace(
              '<%= itemName %>',
              label,
            )}
            chevronButtonRef={chevronButtonRef}
            onClick={toggleDropdown}
            isOpen={isOpen}
            hasSubItems={hasSubItems}
            hasLink={hasLink}
            isDropdownIconOpen={isDropdownIconOpen}
            className={variant === 'srOnly' ? classes.srOnly : undefined}
          />
        ))}
    </div>
  );
};
